'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLang } from './LanguageContext';

export default function UzbekistanSection() {
  const { t } = useLang();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const facts_samarkand = [
  t('uzbek_samarkand_fact1'),
  t('uzbek_samarkand_fact2'),
  t('uzbek_samarkand_fact3')];


  const facts_bukhara = [
  t('uzbek_bukhara_fact1'),
  t('uzbek_bukhara_fact2'),
  t('uzbek_bukhara_fact3')];


  return (
    <section id="uzbekistan" ref={sectionRef} className="py-24 md:py-32 relative overflow-hidden">
      {/* Background decoration */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(196,98,45,0.06) 0%, transparent 70%)'
        }} />
      
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.3), transparent)' }} />
      
      <div className="max-w-8xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-16 md:mb-24">
          <div className="section-label justify-center mb-4 reveal">{t('uzbek_label')}</div>
          <h2
            className="font-serif font-bold text-cream mb-6 reveal reveal-delay-1"
            style={{
              fontFamily: 'Fraunces, serif',
              fontSize: 'clamp(2rem, 5vw, 3.8rem)',
              lineHeight: 1.1
            }}>
            
            {t('uzbek_title')}
          </h2>
          <p className="text-muted text-base md:text-lg leading-relaxed reveal reveal-delay-2">
            {t('uzbek_desc')}
          </p>
        </div>

        {/* Samarkand */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Image */}
          <div className="uzbek-card aspect-[4/3] lg:aspect-auto reveal">
            <AppImage
              src="https://images.unsplash.com/photo-1682862489312-16496aef5fcd"
              alt="Registan Square Samarkand three madrasahs with blue tilework at golden hour"
              fill
              className="object-cover" />
            
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to right, rgba(13,27,42,0.6) 0%, transparent 60%)' }} />
            
            <div className="absolute bottom-6 left-6 glass rounded-xl px-4 py-3">
              <div className="text-gold text-xs font-bold tracking-widest uppercase">UNESCO</div>
              <div className="text-cream text-sm font-medium mt-0.5">Registan Square</div>
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-col justify-center py-4 reveal reveal-delay-2">
            <div className="text-terracotta text-xs font-bold tracking-widest uppercase mb-3">
              🕌 {t('uzbek_samarkand_title')}
            </div>
            <h3
              className="font-serif font-bold text-cream mb-5 text-2xl md:text-3xl lg:text-4xl"
              style={{ fontFamily: 'Fraunces, serif', lineHeight: 1.15 }}>
              
              {t('uzbek_samarkand_title')}
            </h3>
            <p className="text-muted leading-relaxed mb-8 text-base">
              {t('uzbek_samarkand_desc')}
            </p>
            <div className="flex flex-col gap-3">
              {facts_samarkand?.map((fact, i) =>
              <div key={i} className="flex items-center gap-3">
                  <div
                  className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-navy text-xs font-bold"
                  style={{ background: 'var(--gold)' }}>
                  
                    ✓
                  </div>
                  <span className="text-cream/80 text-sm">{fact}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bukhara — reversed */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Content */}
          <div className="flex flex-col justify-center py-4 order-2 lg:order-1 reveal">
            <div className="text-terracotta text-xs font-bold tracking-widest uppercase mb-3">
              🕌 {t('uzbek_bukhara_title')}
            </div>
            <h3
              className="font-serif font-bold text-cream mb-5 text-2xl md:text-3xl lg:text-4xl"
              style={{ fontFamily: 'Fraunces, serif', lineHeight: 1.15 }}>
              
              {t('uzbek_bukhara_title')}
            </h3>
            <p className="text-muted leading-relaxed mb-8 text-base">
              {t('uzbek_bukhara_desc')}
            </p>
            <div className="flex flex-col gap-3 mb-10">
              {facts_bukhara?.map((fact, i) =>
              <div key={i} className="flex items-center gap-3">
                  <div
                  className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-navy text-xs font-bold"
                  style={{ background: 'var(--gold)' }}>
                  
                    ✓
                  </div>
                  <span className="text-cream/80 text-sm">{fact}</span>
                </div>
              )}
            </div>
            <a href="#contact" className="btn-primary self-start">
              {t('uzbek_cta')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>

          {/* Image */}
          <div className="uzbek-card aspect-[4/3] lg:aspect-auto order-1 lg:order-2 reveal reveal-delay-2">
            <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1fbf65c70-1772304049022.png"
              alt="Kalon Minaret Bukhara ancient brick tower at dusk with warm golden light"
              fill
              className="object-cover" />
            
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to left, rgba(13,27,42,0.6) 0%, transparent 60%)' }} />
            
            <div className="absolute bottom-6 right-6 glass rounded-xl px-4 py-3">
              <div className="text-gold text-xs font-bold tracking-widest uppercase">UNESCO</div>
              <div className="text-cream text-sm font-medium mt-0.5">Historic Bukhara</div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.2), transparent)' }} />
      
    </section>);

}